import { useCallback, useState } from 'react';
import { BarChart3, Check } from '../../../icons';
import type { Poll } from '../../../api/chat-polls';
import { useChatPollStore } from '../../../store/chatPollStore';
import './PollCard.css';

/**
 * 대화에 올라간 투표 카드 (chat-polls — 2026-08-26).
 *
 * 말풍선 자리에 그려진다. 고르면 그 자리에서 막대가 갱신되고, 마감은 만든 사람과 방장만 한다
 * (서버가 최종 경계다 — 화면은 같은 경계를 그릴 뿐이다).
 *
 * 익명 투표는 누가 무엇을 골랐는지 서버가 내보내지 않는다. 내 선택만 나에게 돌아온다.
 */
export function PollCard({ poll, canClose }: { poll: Poll; canClose: boolean }) {
  const vote = useChatPollStore((s) => s.vote);
  const close = useChatPollStore((s) => s.close);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mine = new Set(poll.myOptionIds);
  const total = poll.options.reduce((n, o) => n + o.count, 0);
  const closed = poll.closed;
  // 마감 뒤에는 가장 많이 받은 항목을 표시한다 — 결과를 눈으로 바로 찾게.
  const topCount = closed ? Math.max(0, ...poll.options.map((o) => o.count)) : -1;

  const pick = useCallback(async (optionId: string) => {
    if (closed || busy) return;
    setBusy(true);
    setError(null);
    // 하나만 고르는 투표는 고른 것으로 바꾸고, 여러 개면 켜고 끈다.
    const next = poll.multi
      ? (mine.has(optionId)
        ? poll.myOptionIds.filter((id) => id !== optionId)
        : [...poll.myOptionIds, optionId])
      : (mine.has(optionId) ? [] : [optionId]);
    try {
      await vote(poll.id, next);
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      setError(res?.status === 409 ? '마감된 투표입니다.' : res?.data?.message ?? '표를 보내지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [poll, mine, vote, closed, busy]);

  const doClose = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await close(poll.id);
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { message?: string } } })?.response;
      setError(res?.data?.message ?? '마감하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }, [poll.id, close]);

  // 참여 인원은 카드 아래 한 곳에서만 말한다 — 위아래로 같은 말이 두 번 나오면 지저분하다.
  const meta = [
    poll.anonymous ? '익명' : null,
    poll.multi ? '여러 개 고르기' : '하나만 고르기',
    poll.closesAt ? `${formatDeadline(poll.closesAt)} ${closed ? '마감됨' : '마감'}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={`chat-poll${closed ? ' is-closed' : ''}`} role="group" aria-label={`투표 ${poll.question}`}>
      <div className="chat-poll-head">
        <div className="chat-poll-kicker">
          <BarChart3 size={12} aria-hidden />
          <span>투표 · {closed ? '마감됨' : '진행 중'}</span>
        </div>
        <div className="chat-poll-q">{poll.question}</div>
        {meta && <div className="chat-poll-meta">{meta}</div>}
      </div>

      <div className="chat-poll-opts">
        {poll.options.map((o) => {
          const on = mine.has(o.id);
          const pct = total > 0 ? Math.round((o.count / total) * 100) : 0;
          const win = closed && o.count > 0 && o.count === topCount;
          return (
            <button
              key={o.id}
              type="button"
              className={`chat-poll-opt${on ? ' is-mine' : ''}${win ? ' is-win' : ''}`}
              onClick={() => void pick(o.id)}
              disabled={closed || busy}
              aria-pressed={on}
            >
              {/* 막대는 글자 뒤에 깔린다 — 폭이 0이어도 줄 높이가 흔들리지 않게 절대배치. */}
              <span className="chat-poll-fill" style={{ width: `${pct}%` }} aria-hidden />
              <span className="chat-poll-row">
                {!closed && (
                  <span className={`chat-poll-check${on ? ' is-on' : ''}`} aria-hidden>
                    {on && <Check size={9} />}
                  </span>
                )}
                <span className="chat-poll-name">{o.text}</span>
                <span className="chat-poll-pct">{o.count}명 · {pct}%</span>
              </span>
            </button>
          );
        })}
      </div>

      {error && <p className="chat-poll-err" role="alert">{error}</p>}

      <div className="chat-poll-foot">
        <span>{poll.voterCount}명 참여</span>
        {!closed && canClose && (
          <button type="button" className="chat-poll-close" onClick={() => void doClose()} disabled={busy}>
            투표 마감
          </button>
        )}
      </div>
    </div>
  );
}

/** 마감 시각 — 오늘·내일은 시각만, 그 밖은 날짜까지. */
function formatDeadline(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (sameDay) return `오늘 ${time}`;
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${time}`;
}
